export interface RoomTemplate {
  id: string
  name: string
  type: 'bedroom' | 'bathroom' | 'kitchen' | 'living' | 'dining' | 'office' | 'storage'
  minSize: { width: number; depth: number }
  recommendedSize: { width: number; depth: number }
  maxSize: { width: number; depth: number }
  features: string[]
  description: string
  cost: number
}

export interface BathroomConfiguration {
  id: string
  name: string
  description: string
  fixtures: string[]
  minSize: { width: number; depth: number }
  cost: number
  accessibility?: boolean
}

export interface ADUTemplate {
  id: string
  name: string
  description: string
  category: 'studio' | 'one-bedroom' | 'two-bedroom' | 'three-bedroom' | 'custom'
  totalSize: { width: number; depth: number; height: number }
  rooms: {
    bedrooms: number
    bathrooms: number
    hasKitchen: boolean
    hasLivingRoom: boolean
    hasDining: boolean
    hasOffice: boolean
    hasStorage: boolean
  }
  bathroomConfig: BathroomConfiguration[]
  roomLayout: RoomTemplate[]
  estimatedCost: number
  buildTime: string
  popularity: number
  image?: string
}

export const BATHROOM_CONFIGURATIONS: BathroomConfiguration[] = [
  {
    id: 'powder-room',
    name: 'Powder Room',
    description: 'Compact half-bath with toilet and sink',
    fixtures: ['toilet', 'pedestal-sink'],
    minSize: { width: 4, depth: 3 },
    cost: 8000
  },
  {
    id: 'standard-full',
    name: 'Standard Full Bath',
    description: 'Full bathroom with tub/shower combo',
    fixtures: ['toilet', 'vanity-sink', 'tub-shower-combo'],
    minSize: { width: 5, depth: 8 },
    cost: 15000
  },
  {
    id: 'luxury-full',
    name: 'Luxury Full Bath',
    description: 'Spacious bathroom with separate shower and tub',
    fixtures: ['toilet', 'double-vanity', 'separate-shower', 'soaking-tub'],
    minSize: { width: 8, depth: 10 },
    cost: 25000
  },
  {
    id: 'accessible-full',
    name: 'ADA Accessible Bath',
    description: 'Wheelchair accessible with grab bars and roll-in shower',
    fixtures: ['comfort-height-toilet', 'accessible-vanity', 'roll-in-shower', 'grab-bars'],
    minSize: { width: 8, depth: 8 },
    cost: 20000,
    accessibility: true
  },
  {
    id: 'ensuite-master',
    name: 'Master Ensuite',
    description: 'Luxurious master bathroom with premium fixtures',
    fixtures: ['toilet', 'double-vanity', 'walk-in-shower', 'freestanding-tub', 'heated-floors'],
    minSize: { width: 10, depth: 12 },
    cost: 35000
  }
]

export const ROOM_TEMPLATES: RoomTemplate[] = [
  // Bedrooms
  {
    id: 'bedroom-small',
    name: 'Small Bedroom',
    type: 'bedroom',
    minSize: { width: 8, depth: 9 },
    recommendedSize: { width: 10, depth: 11 },
    maxSize: { width: 12, depth: 13 },
    features: ['closet', 'window', 'electrical-outlets'],
    description: 'Cozy bedroom suitable for single occupancy',
    cost: 8000
  },
  {
    id: 'bedroom-standard',
    name: 'Standard Bedroom',
    type: 'bedroom',
    minSize: { width: 10, depth: 11 },
    recommendedSize: { width: 12, depth: 13 },
    maxSize: { width: 14, depth: 15 },
    features: ['walk-in-closet', 'large-window', 'electrical-outlets', 'ceiling-fan'],
    description: 'Comfortable bedroom for couples or spacious single use',
    cost: 12000
  },
  {
    id: 'bedroom-master',
    name: 'Master Bedroom',
    type: 'bedroom',
    minSize: { width: 12, depth: 14 },
    recommendedSize: { width: 14, depth: 16 },
    maxSize: { width: 16, depth: 18 },
    features: ['walk-in-closet', 'ensuite-bathroom', 'large-windows', 'electrical-outlets', 'ceiling-fan', 'sitting-area'],
    description: 'Spacious master bedroom with luxury features',
    cost: 18000
  },

  // Living Areas
  {
    id: 'living-compact',
    name: 'Compact Living Room',
    type: 'living',
    minSize: { width: 10, depth: 12 },
    recommendedSize: { width: 12, depth: 14 },
    maxSize: { width: 14, depth: 16 },
    features: ['large-windows', 'electrical-outlets', 'cable-ready'],
    description: 'Efficient living space perfect for relaxation',
    cost: 6000
  },
  {
    id: 'living-open',
    name: 'Open Living Area',
    type: 'living',
    minSize: { width: 14, depth: 16 },
    recommendedSize: { width: 16, depth: 18 },
    maxSize: { width: 20, depth: 22 },
    features: ['large-windows', 'electrical-outlets', 'cable-ready', 'fireplace-ready', 'high-ceilings'],
    description: 'Spacious open-concept living area',
    cost: 10000
  },

  // Kitchens
  {
    id: 'kitchen-galley',
    name: 'Galley Kitchen',
    type: 'kitchen',
    minSize: { width: 6, depth: 10 },
    recommendedSize: { width: 7, depth: 12 },
    maxSize: { width: 8, depth: 14 },
    features: ['upper-cabinets', 'lower-cabinets', 'countertops', 'sink', 'appliance-ready'],
    description: 'Efficient galley-style kitchen layout',
    cost: 15000
  },
  {
    id: 'kitchen-l-shape',
    name: 'L-Shaped Kitchen',
    type: 'kitchen',
    minSize: { width: 8, depth: 10 },
    recommendedSize: { width: 10, depth: 12 },
    maxSize: { width: 12, depth: 14 },
    features: ['upper-cabinets', 'lower-cabinets', 'island-ready', 'countertops', 'sink', 'appliance-package'],
    description: 'Versatile L-shaped kitchen with good workflow',
    cost: 20000
  },
  {
    id: 'kitchen-luxury',
    name: 'Luxury Kitchen',
    type: 'kitchen',
    minSize: { width: 12, depth: 14 },
    recommendedSize: { width: 14, depth: 16 },
    maxSize: { width: 16, depth: 18 },
    features: ['custom-cabinets', 'quartz-countertops', 'kitchen-island', 'premium-appliances', 'pantry', 'wine-fridge-ready'],
    description: 'High-end kitchen with premium finishes and appliances',
    cost: 35000
  },

  // Other Rooms
  {
    id: 'office-small',
    name: 'Home Office',
    type: 'office',
    minSize: { width: 6, depth: 8 },
    recommendedSize: { width: 8, depth: 10 },
    maxSize: { width: 10, depth: 12 },
    features: ['built-in-desk', 'electrical-outlets', 'ethernet-ready', 'window', 'storage'],
    description: 'Dedicated workspace for remote work',
    cost: 5000
  },
  {
    id: 'storage-room',
    name: 'Storage Room',
    type: 'storage',
    minSize: { width: 4, depth: 6 },
    recommendedSize: { width: 6, depth: 8 },
    maxSize: { width: 8, depth: 10 },
    features: ['shelving', 'electrical-outlet', 'ventilation'],
    description: 'Utility storage space',
    cost: 2000
  }
]

export const ADU_TEMPLATES: ADUTemplate[] = [
  {
    id: 'studio-compact',
    name: 'Compact Studio',
    description: 'Efficient studio perfect for single occupancy',
    category: 'studio',
    totalSize: { width: 16, depth: 20, height: 9 },
    rooms: {
      bedrooms: 0,
      bathrooms: 1,
      hasKitchen: true,
      hasLivingRoom: true,
      hasDining: false,
      hasOffice: false,
      hasStorage: true
    },
    bathroomConfig: [BATHROOM_CONFIGURATIONS[1]], // Standard full bath
    roomLayout: [
      ROOM_TEMPLATES.find(r => r.id === 'living-compact')!,
      ROOM_TEMPLATES.find(r => r.id === 'kitchen-galley')!,
      ROOM_TEMPLATES.find(r => r.id === 'storage-room')!
    ],
    estimatedCost: 85000,
    buildTime: '8-12 weeks',
    popularity: 85
  },
  {
    id: 'studio-luxury',
    name: 'Luxury Studio',
    description: 'High-end studio with premium finishes',
    category: 'studio',
    totalSize: { width: 20, depth: 24, height: 10 },
    rooms: {
      bedrooms: 0,
      bathrooms: 1,
      hasKitchen: true,
      hasLivingRoom: true,
      hasDining: false,
      hasOffice: true,
      hasStorage: true
    },
    bathroomConfig: [BATHROOM_CONFIGURATIONS[2]], // Luxury full bath
    roomLayout: [
      ROOM_TEMPLATES.find(r => r.id === 'living-open')!,
      ROOM_TEMPLATES.find(r => r.id === 'kitchen-l-shape')!,
      ROOM_TEMPLATES.find(r => r.id === 'office-small')!,
      ROOM_TEMPLATES.find(r => r.id === 'storage-room')!
    ],
    estimatedCost: 125000,
    buildTime: '10-14 weeks',
    popularity: 70
  },
  {
    id: 'one-bedroom-standard',
    name: 'Standard 1-Bedroom',
    description: 'Classic one-bedroom layout with separate living areas',
    category: 'one-bedroom',
    totalSize: { width: 24, depth: 28, height: 9 },
    rooms: {
      bedrooms: 1,
      bathrooms: 1,
      hasKitchen: true,
      hasLivingRoom: true,
      hasDining: false,
      hasOffice: false,
      hasStorage: true
    },
    bathroomConfig: [BATHROOM_CONFIGURATIONS[1]], // Standard full bath
    roomLayout: [
      ROOM_TEMPLATES.find(r => r.id === 'bedroom-standard')!,
      ROOM_TEMPLATES.find(r => r.id === 'living-compact')!,
      ROOM_TEMPLATES.find(r => r.id === 'kitchen-l-shape')!,
      ROOM_TEMPLATES.find(r => r.id === 'storage-room')!
    ],
    estimatedCost: 145000,
    buildTime: '12-16 weeks',
    popularity: 95
  },
  {
    id: 'one-bedroom-luxury',
    name: 'Luxury 1-Bedroom',
    description: 'Premium one-bedroom with high-end finishes',
    category: 'one-bedroom',
    totalSize: { width: 28, depth: 32, height: 10 },
    rooms: {
      bedrooms: 1,
      bathrooms: 1,
      hasKitchen: true,
      hasLivingRoom: true,
      hasDining: true,
      hasOffice: true,
      hasStorage: true
    },
    bathroomConfig: [BATHROOM_CONFIGURATIONS[2]], // Luxury full bath
    roomLayout: [
      ROOM_TEMPLATES.find(r => r.id === 'bedroom-master')!,
      ROOM_TEMPLATES.find(r => r.id === 'living-open')!,
      ROOM_TEMPLATES.find(r => r.id === 'kitchen-luxury')!,
      ROOM_TEMPLATES.find(r => r.id === 'office-small')!,
      ROOM_TEMPLATES.find(r => r.id === 'storage-room')!
    ],
    estimatedCost: 195000,
    buildTime: '14-18 weeks',
    popularity: 75
  },
  {
    id: 'two-bedroom-family',
    name: 'Family 2-Bedroom',
    description: 'Perfect for small families with two bedrooms',
    category: 'two-bedroom',
    totalSize: { width: 32, depth: 36, height: 9 },
    rooms: {
      bedrooms: 2,
      bathrooms: 2,
      hasKitchen: true,
      hasLivingRoom: true,
      hasDining: true,
      hasOffice: false,
      hasStorage: true
    },
    bathroomConfig: [
      BATHROOM_CONFIGURATIONS[1], // Standard full bath
      BATHROOM_CONFIGURATIONS[0]  // Powder room
    ],
    roomLayout: [
      ROOM_TEMPLATES.find(r => r.id === 'bedroom-master')!,
      ROOM_TEMPLATES.find(r => r.id === 'bedroom-standard')!,
      ROOM_TEMPLATES.find(r => r.id === 'living-open')!,
      ROOM_TEMPLATES.find(r => r.id === 'kitchen-l-shape')!,
      ROOM_TEMPLATES.find(r => r.id === 'storage-room')!
    ],
    estimatedCost: 225000,
    buildTime: '16-20 weeks',
    popularity: 90
  },
  {
    id: 'two-bedroom-accessible',
    name: 'Accessible 2-Bedroom',
    description: 'ADA compliant design with accessibility features',
    category: 'two-bedroom',
    totalSize: { width: 34, depth: 38, height: 9 },
    rooms: {
      bedrooms: 2,
      bathrooms: 2,
      hasKitchen: true,
      hasLivingRoom: true,
      hasDining: true,
      hasOffice: false,
      hasStorage: true
    },
    bathroomConfig: [
      BATHROOM_CONFIGURATIONS[3], // ADA accessible bath
      BATHROOM_CONFIGURATIONS[0]  // Powder room
    ],
    roomLayout: [
      ROOM_TEMPLATES.find(r => r.id === 'bedroom-master')!,
      ROOM_TEMPLATES.find(r => r.id === 'bedroom-standard')!,
      ROOM_TEMPLATES.find(r => r.id === 'living-open')!,
      ROOM_TEMPLATES.find(r => r.id === 'kitchen-l-shape')!,
      ROOM_TEMPLATES.find(r => r.id === 'storage-room')!
    ],
    estimatedCost: 245000,
    buildTime: '16-20 weeks',
    popularity: 60
  }
]

export const getTemplatesByCategory = (category: ADUTemplate['category']) => {
  return ADU_TEMPLATES.filter(template => template.category === category)
}

export const getTemplateById = (id: string) => {
  return ADU_TEMPLATES.find(template => template.id === id)
}

export const getBathroomConfigById = (id: string) => {
  return BATHROOM_CONFIGURATIONS.find(config => config.id === id)
}

export const getRoomTemplateById = (id: string) => {
  return ROOM_TEMPLATES.find(room => room.id === id)
}
